import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { TraitBars } from "../components/Charts";
import ShareButton from "../components/ShareButton";
import { Download, Sparkles, Puzzle, Wrench, Heart, Briefcase, ArrowRight, Loader2 } from "lucide-react";

function Card({ children, className = "" }) {
  return <div className={`bg-white border border-[#E8E1D9] rounded-2xl p-6 sm:p-8 shadow-sm ${className}`}>{children}</div>;
}
function BulletList({ items, color }) {
  return (
    <ul className="space-y-2.5">
      {items?.map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-[#2B2625]">
          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
          <span className="text-[15px] leading-relaxed">{t}</span>
        </li>
      ))}
    </ul>
  );
}
function Skeleton({ lines = 3 }) {
  return (
    <div className="space-y-2.5 animate-pulse" data-testid="combo-skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-3.5 rounded-full bg-[#EFE7DD]" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export default function Combination() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await api.combination(id);
        if (!alive) return;
        setData(d);
        if (d.status !== "ready") timer.current = setTimeout(load, 3000);
      } catch { /* ignore */ }
    };
    load();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [id]);

  if (!data) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[#918780]">Loading your shared report…</div>;
  const { name1, name2, relationship, compat, narrative, status } = data;
  const ready = status === "ready" && narrative;
  const areaItems = Object.keys(compat.areas).map((k) => ({ label: k, value: compat.areas[k] }));

  return (
    <div className="max-w-4xl mx-auto px-5 py-10 space-y-6">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-3xl bg-[#3F3A44] text-white p-8 sm:p-11 text-center relative overflow-hidden">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#E4D6F5]">Your compatibility report</span>
        <h1 className="font-display text-2xl sm:text-4xl font-bold mt-2" data-testid="combo-headline">
          {ready ? narrative.headline : `${name1} & ${name2}`}
        </h1>
        <p className="text-[#E4D6F5] mt-2">{name1} & {name2} · as {relationship}s</p>
        <div className="mt-6 inline-flex flex-col items-center">
          <span className="font-display text-6xl sm:text-7xl font-bold text-[#7C3AED]" data-testid="combination-score">
            {compat.overall}%
          </span>
          <span className="font-semibold text-white/90 mt-1">{compat.band}</span>
        </div>
        <div className="mt-6 flex justify-center">
          <ShareButton url={`/combination/${id}`} variant="dark"
            title={`${name1} & ${name2} — ${compat.overall}% compatible`}
            text={`${name1} & ${name2} scored ${compat.overall}% (${compat.band}) on RelateMetrics! See how you connect:`} />
        </div>
      </motion.div>

      {!ready && (
        <div className="flex items-center gap-3 rounded-xl bg-[#FEF6E8] text-[#3B0764] px-4 py-3 text-sm font-medium" data-testid="combo-generating">
          <Loader2 className="animate-spin" size={16} /> Your score is in! We're writing your shared insights — just a few seconds…
        </div>
      )}

      <Card>
        <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-5">Where you meet, area by area</h3>
        <TraitBars items={areaItems} color="#6B21A8" />
      </Card>

      <Card>
        <div className="flex items-center gap-2 text-[#3F3A44] font-semibold mb-3"><Sparkles size={17} /> How you connect</div>
        {ready ? <p className="text-[#2B2625] text-lg leading-relaxed">{narrative.connection_summary}</p> : <Skeleton lines={3} />}
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center gap-2 text-[#3F3A44] font-semibold mb-4"><Puzzle size={17} /> Where you complement each other</div>
          {ready ? <BulletList items={narrative.complementary_areas} color="#3F3A44" /> : <Skeleton lines={3} />}
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-[#7C3AED] font-semibold mb-4"><Wrench size={17} /> Areas to build together</div>
          {ready ? <BulletList items={narrative.build_areas} color="#7C3AED" /> : <Skeleton lines={3} />}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-4"><Heart size={17} /> For your relationship</div>
          {ready ? <BulletList items={narrative.relationship_tips} color="#6B21A8" /> : <Skeleton lines={3} />}
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-4"><Briefcase size={17} /> For working together</div>
          {ready ? <BulletList items={narrative.work_tips} color="#6B21A8" /> : <Skeleton lines={3} />}
        </Card>
      </div>

      {ready && (
        <Card className="bg-[#FEF6E8] border-[#EADBF9]">
          <p className="font-display text-xl sm:text-2xl text-[#6B21A8] italic text-center leading-relaxed">
            "{narrative.closing}"
          </p>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        {ready ? (
          <a href={api.combinationPdf(id)} target="_blank" rel="noreferrer" data-testid="combination-download-pdf"
             className="inline-flex items-center gap-2 bg-[#F2E9FA] hover:bg-[#E4D6F5] text-[#3F3A44] font-semibold py-3 px-6 rounded-xl transition-colors">
            <Download size={18} /> Download PDF
          </a>
        ) : (
          <span className="inline-flex items-center gap-2 bg-[#F3EDE4] text-[#918780] font-semibold py-3 px-6 rounded-xl">
            <Loader2 className="animate-spin" size={16} /> Preparing PDF…
          </span>
        )}
        <Link to="/dashboard" className="text-[#6E655F] hover:text-[#6B21A8] font-medium px-2">Dashboard</Link>
      </div>

      <div className="rounded-3xl bg-[#6B21A8] text-white p-8 sm:p-10 text-center">
        <h3 className="font-display text-2xl font-bold mb-2">Curious about someone else?</h3>
        <p className="text-[#F3E3FC] mb-6 max-w-lg mx-auto">Every new connection reveals something new about you too. Invite one more person to explore.</p>
        <Link to="/invite" data-testid="combination-invite-another"
          className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#5B21B6] text-[#2B2625] font-semibold py-3.5 px-8 rounded-xl transition-colors active:scale-[0.98]">
          Invite another person <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}
